// Per-file calibration status chip for the observation file cards. Renders one
// of four mutually-exclusive states, in precedence:
//   1. calibrated              → the green "Cal" chip (the frame WAS calibrated)
//   2. not calibratable        → the existing "No Cal" chip (MissingCalibrationChip)
//   3. calibratable but stale   → "Cal · stale" (only match is an expired master)
//   4. calibratable and clean   → the orange "Cal Ready" chip: matching masters exist,
//      but this frame has not been calibrated (e.g. it was processed before they were
//      built); Download Calibrated calibrates it on the fly.
// `calibratable` says whether the frame COULD be calibrated now; only
// `calibration_status` says whether it WAS -- the chip used to show green "Cal" for
// both, so frames processed before their masters existed looked calibrated.
// Reuses MissingCalibrationChip verbatim for state 1 so the "missing" wording
// and look stay single-sourced (it self-gates to null when calibratable=true).
//
// Used by the two user observation views (ObservationDetail, ObservationFiles).
// The operator preview (EmbeddedObservationDetail) and the "My Files" grid
// (FileGrid) render their own chips off different coverage signals and do NOT
// surface the stale state yet — see the follow-ups noted in the fix plan.
//
// Name avoids collision with calibrationBlockedReason.js's `calibrationStatusChip`
// (that one is for calibration-library accumulation groups, not per-image files).

import React from 'react';
import { Chip, Tooltip, Box } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import MissingCalibrationChip from './MissingCalibrationChip';
import { staleCalibrationReason } from '../../utils/staleCalibrationReason';

// The frame was calibrated. `calibration_status` is the stored outcome; `preview_calibrated`
// covers responses from servers that predate the field.
export const isFileCalibrated = (file) => (
    file?.calibration_status === 'calibrated'
    || (file?.calibration_status == null && file?.preview_calibrated === true)
);

/**
 * @param {{ file: object, sx?: object }} props
 *   `file` is a userfile shape with `calibratable` + `calibratability` set by
 *   userFilesRoutes. `sx` is merged onto the green/stale chips so callers can
 *   size the chip to their card (the "No Cal" branch keeps its own styling).
 */
const FileCalibrationChip = ({ file, sx }) => {
    // State 1: calibrated → green "Cal".
    if (isFileCalibrated(file)) {
        return (
            <Tooltip title="Calibrated" placement="top">
                <Chip
                    data-testid="file-cal-chip"
                    label="Cal"
                    size="small"
                    color="success"
                    sx={{ height: 20, fontSize: '0.65rem', ...sx }}
                />
            </Tooltip>
        );
    }
    // State 2: not calibratable → delegate to the existing chip (may be null).
    if (!file?.calibratable) {
        return <MissingCalibrationChip file={file} />;
    }

    // State 3: calibratable but only an expired master matched.
    const stale = staleCalibrationReason(file);
    if (stale) {
        const tooltipContent = (
            <Box sx={{ whiteSpace: 'pre-line' }}>
                {stale.tooltipLines.join('\n')}
            </Box>
        );
        return (
            <Tooltip title={tooltipContent} placement="top">
                <Chip
                    label={stale.label}
                    size="small"
                    color={stale.color}
                    icon={<HistoryIcon />}
                    variant="outlined"
                    sx={sx}
                />
            </Tooltip>
        );
    }

    // State 4: matching masters exist but the frame hasn't been calibrated → orange
    // "Cal Ready". Positioning and opacity come from the wrapping Box at the call
    // site (see the two render sites), so this only carries sizing.
    return (
        <Tooltip title="Calibration ready: matching masters exist, but this frame hasn't been calibrated yet. Download Calibrated calibrates it." placement="top">
            <Chip
                data-testid="file-cal-chip"
                label="Cal Ready"
                size="small"
                color="warning"
                sx={{ height: 20, fontSize: '0.65rem', ...sx }}
            />
        </Tooltip>
    );
};

export default FileCalibrationChip;
